import { Inject, Injectable } from '@angular/core';
import { DB_ALLDOCS_QUERY_OPTIONS, DB_KEY_PREFIXES, DB_SYNC_STATE_KEYS, OFFLINE_DATA_COUNT_ENTITY_NAME } from '@omni/config/pouch-db.config';
import { APPOINTMENT_REF_RESOLVER, AppointmentRefResolver } from '@omni/services/meeting/appointment-ref-resolver';
import {
  MeetingExpenseDoc,
  OnlineNoteChange,
  applyOnlineNoteChange,
  expenseFromDoc,
  isOfflineMeetingId,
  meetingKey,
  mergeSyncedExpenses,
  stageCreate,
  stageFieldEdit,
  stageNoteRemove,
  stageNoteUpsert,
  stageScrap,
  stageSubmit,
  toStoredExpense,
} from '@omni/classes/meeting-expense/meeting-expense.pending';
import { DynamicsClientService } from '../dynamics-client/dynamics-client.service';
import { DiskService } from '@omni/services/disk/disk.service';
import { MEETING_EXPENSE_FETCH_XML, MeetingSyncWindow, chunkIds, meetingExpenseSyncFetchXml, meetingExpenseSyncIdsFetchXml, toFetchInValues } from '@omni/config/fetch-xml/meeting-expense.fetch-xml';
import { IONote } from '@omni/classes/io/io-note.class';
import { withTimeout } from '@omni/utility/util';
import type { StagedSdrNote } from '../sample/sample-delivery-request.data.service';
import {
  ExpenseMasterData,
  ExpenseOption,
  MeetingExpense,
  applyScrap,
  applySubmit,
  buildExpenseCreatePayload,
  buildExpenseFieldPayload,
  buildExpenseScrapPayload,
  buildExpenseSubmitPayload,
  isExpenseCancelled,
  meetingExpenseFromRaw,
} from '@omni/classes/meeting-expense/meeting-expense.class';
import {
  ANNOTATIONS_ENTITY_SET,
  EXPENSE_SUBTYPE_ENTITY_SET,
  EXPENSE_TYPE_ENTITY_SET,
  MEETING_EXPENSE_ENTITY_SET,
  MEETING_EXPENSE_FIELDS,
  MEETING_EXPENSE_NAV,
} from '@omni/classes/meeting-expense/meeting-expense.constants';

const FORMATTED = '@OData.Community.Display.V1.FormattedValue';

const WRITE_TIMEOUT_MS = 60000;

@Injectable({ providedIn: 'root' })
export class MeetingExpenseDataService {
  // Kept in step with every write, so a write never re-reads every expense (and its offline files) to count.
  private pendingIds: Set<string> | null = null;
  // Writes that land while a recount is scanning, replayed onto its result so they are not lost.
  private readonly recountChanges = new Set<Map<string, boolean>>();

  constructor(
    private readonly dynamics: DynamicsClientService,
    private readonly disk: DiskService,
    @Inject(APPOINTMENT_REF_RESOLVER) private readonly resolveAppointmentRef: AppointmentRefResolver,
  ) {}

  async fetchMasterData(): Promise<ExpenseMasterData> {
    const [types, subtypes, pairs] = await Promise.all([
      this.fetchOptions(EXPENSE_TYPE_ENTITY_SET, MEETING_EXPENSE_FETCH_XML.meetingTypes, 'indskr_expense_typeid'),
      this.fetchOptions(EXPENSE_SUBTYPE_ENTITY_SET, MEETING_EXPENSE_FETCH_XML.meetingSubtypes, 'indskr_expense_categoryid'),
      this.dynamics.executeFetchXml<any>(EXPENSE_TYPE_ENTITY_SET, MEETING_EXPENSE_FETCH_XML.typeSubtypePairs),
    ]);
    const master: ExpenseMasterData = {
      types,
      subtypes,
      pairs: (pairs?.value ?? []).map((row) => ({ typeId: row.indskr_expense_typeid, subtypeId: row.subtypeId })),
    };
    await this.disk.updateOrInsert(DB_KEY_PREFIXES.MEETING_EXPENSE_MASTER, () => ({ master }));
    return master;
  }

  async getCachedMasterData(): Promise<ExpenseMasterData | null> {
    const doc = await this.disk.retrieve(DB_KEY_PREFIXES.MEETING_EXPENSE_MASTER, true);
    return doc?.master ?? null;
  }

  async fetchForMeeting(meetingId: string): Promise<MeetingExpense[]> {
    const local = await this.getDocsForMeeting(meetingId);
    const raws = await this.fetchRaws(MEETING_EXPENSE_FETCH_XML.byMeeting.replace('{meetingId}', () => meetingId));
    const notesByExpense = await this.fetchNotes(raws.map((raw) => raw.indskr_expenseid)).catch((err) => {
      console.error('MeetingExpenseDataService.fetchNotes failed', err);
      return new Map<string, IONote[]>();
    });
    const pending = local.filter((doc) => !!doc.pending);
    const pendingIds = new Set(pending.map((doc) => doc.expense.id));
    const server = raws
      .map((raw) => meetingExpenseFromRaw(raw, meetingId, notesByExpense.get(raw.indskr_expenseid) ?? []))
      .filter((expense) => !isExpenseCancelled(expense) && !pendingIds.has(expense.id));
    // The server omits expenses cancelled or deleted elsewhere; their cached docs would resurface offline as Drafts.
    const serverIds = new Set(server.map((expense) => expense.id));
    await Promise.all([
      ...server.map((expense) => this.cacheExpense(expense)),
      ...local.filter((doc) => !doc.pending && !serverIds.has(doc.expense.id)).map((doc) => this.removeCached(doc.expense.id)),
    ]);
    const localOnly = pending.map(expenseFromDoc).filter((expense) => !isExpenseCancelled(expense));
    return [...server, ...localOnly];
  }

  async getCachedForMeeting(meetingId: string): Promise<MeetingExpense[]> {
    return (await this.getDocsForMeeting(meetingId)).map(expenseFromDoc).filter((expense) => !isExpenseCancelled(expense));
  }

  /** Every cached expense, with offline meeting ids replaced by real ones where the meeting has synced. */
  async getAllCached(): Promise<MeetingExpense[]> {
    return (await this.allDocs()).map((doc) => {
      const expense = expenseFromDoc(doc);
      return { ...expense, meetingId: this.resolveMeeting(expense.meetingId) ?? expense.meetingId };
    });
  }

  resolveMeeting(meetingId: string): string | null {
    return isOfflineMeetingId(meetingId) ? this.resolveAppointmentRef(meetingId) : meetingId;
  }

  async getDoc(expenseId: string): Promise<MeetingExpenseDoc | null> {
    const doc = await this.disk.retrieve(this.recordKey(expenseId), true);
    return doc?.expense ? { expense: doc.expense, pending: doc.pending } : null;
  }

  async putDoc(doc: MeetingExpenseDoc): Promise<void> {
    await this.disk.updateOrInsert(this.recordKey(doc.expense.id), () => ({ expense: toStoredExpense(doc.expense), pending: doc.pending }));
    await this.trackPending(doc.expense.id, !!doc.pending);
  }

  async isPending(expenseId: string): Promise<boolean> {
    if (this.pendingIds) return this.pendingIds.has(expenseId);
    return !!(await this.getDoc(expenseId))?.pending;
  }

  async getPendingDocs(): Promise<MeetingExpenseDoc[]> {
    return (await this.allDocs()).filter((doc) => !!doc.pending);
  }

  async recountPending(): Promise<void> {
    const changes = new Map<string, boolean>();
    this.recountChanges.add(changes);
    try {
      const ids = new Set((await this.getPendingDocs()).map((doc) => doc.expense.id));
      for (const [id, isPending] of changes) {
        if (isPending) ids.add(id);
        else ids.delete(id);
      }
      this.pendingIds = ids;
    } finally {
      this.recountChanges.delete(changes);
    }
    this.publishPendingCount();
  }

  /** Drops what the server can resend; unsynced changes stay so they still upload. */
  async purgeSynced(): Promise<void> {
    for (const doc of await this.allDocs()) {
      if (!doc.pending) await this.removeCached(doc.expense.id);
    }
    // DiskService.remove logs an error for a missing key, and this runs on every sync without the feature action.
    for (const key of [DB_KEY_PREFIXES.MEETING_EXPENSE_MASTER, DB_SYNC_STATE_KEYS.SYNC_MEETING_EXPENSES]) {
      if (await this.disk.retrieve(key, true, true)) await this.disk.remove(key);
    }
  }

  /** `createSent` when a create may already have reached the server. */
  async stageCreate(expense: MeetingExpense, createSent = false): Promise<MeetingExpense> {
    const doc = stageCreate(expense, createSent);
    await this.putDoc(doc);
    return expenseFromDoc(doc);
  }

  async stageFields(expense: MeetingExpense): Promise<void> {
    await this.mutate(expense.id, (current) => stageFieldEdit(current, expense));
  }

  async stageSubmit(expense: MeetingExpense): Promise<MeetingExpense> {
    const doc = await this.mutate(expense.id, (current) => stageSubmit(stageFieldEdit(current, expense)));
    return doc ? expenseFromDoc(doc) : applySubmit(expense);
  }

  async stageScrap(expense: MeetingExpense): Promise<MeetingExpense> {
    await this.mutate(expense.id, (current) => stageScrap(current ?? { expense }));
    return applyScrap(expense);
  }

  async stageNote(expenseId: string, note: StagedSdrNote, clearAttachment: boolean): Promise<void> {
    await this.mutate(expenseId, (current) => stageNoteUpsert(this.requireDoc(current), note, clearAttachment));
  }

  async stageNoteRemove(expenseId: string, note: StagedSdrNote): Promise<void> {
    await this.mutate(expenseId, (current) => stageNoteRemove(this.requireDoc(current), note));
  }

  async create(expense: MeetingExpense, currencyId: string | null): Promise<void> {
    await this.remoteCreate(expense, currencyId);
    await this.cacheExpense(expense);
  }

  async update(expense: MeetingExpense): Promise<void> {
    await this.remoteUpdate(expense);
    await this.cacheExpense(expense, true);
  }

  async submit(expense: MeetingExpense): Promise<MeetingExpense> {
    await withTimeout(
      this.dynamics.update(expense.id, MEETING_EXPENSE_ENTITY_SET, { ...buildExpenseFieldPayload(expense), ...buildExpenseSubmitPayload() }),
      WRITE_TIMEOUT_MS,
      'Expense submit',
    );
    const submitted = { ...applySubmit(expense), pendingSync: false };
    await this.cacheExpense(submitted, true);
    return submitted;
  }

  async scrap(expense: MeetingExpense): Promise<MeetingExpense> {
    await withTimeout(
      this.dynamics.update(expense.id, MEETING_EXPENSE_ENTITY_SET, buildExpenseScrapPayload()),
      WRITE_TIMEOUT_MS,
      'Expense scrap',
    );
    await this.removeCached(expense.id);
    return applyScrap(expense);
  }

  remoteCreate(expense: MeetingExpense, currencyId: string | null): Promise<unknown> {
    return withTimeout(this.dynamics.create(buildExpenseCreatePayload(expense, currencyId), MEETING_EXPENSE_ENTITY_SET), WRITE_TIMEOUT_MS, 'Expense create');
  }

  remoteUpdate(expense: MeetingExpense): Promise<unknown> {
    return withTimeout(this.dynamics.update(expense.id, MEETING_EXPENSE_ENTITY_SET, buildExpenseFieldPayload(expense)), WRITE_TIMEOUT_MS, 'Expense update');
  }

  remoteSubmit(expenseId: string): Promise<unknown> {
    return withTimeout(this.dynamics.update(expenseId, MEETING_EXPENSE_ENTITY_SET, buildExpenseSubmitPayload()), WRITE_TIMEOUT_MS, 'Expense submit');
  }

  remoteScrap(expenseId: string): Promise<unknown> {
    return withTimeout(this.dynamics.update(expenseId, MEETING_EXPENSE_ENTITY_SET, buildExpenseScrapPayload()), WRITE_TIMEOUT_MS, 'Expense scrap');
  }

  /** The server's copy of each id it has, keyed by lower-case id; Cancelled included. */
  async fetchServerExpenses(expenseIds: string[]): Promise<Map<string, MeetingExpense>> {
    const byId = new Map<string, MeetingExpense>();
    for (const chunk of chunkIds(expenseIds)) {
      const fetchXml = MEETING_EXPENSE_FETCH_XML.byIds.replace('{idValues}', () => toFetchInValues(chunk));
      for (const raw of await this.fetchRaws(fetchXml)) {
        const expense = meetingExpenseFromRaw(raw, meetingKey(raw[`_${MEETING_EXPENSE_FIELDS.REGARDING}_value`]));
        byId.set(meetingKey(expense.id), expense);
      }
    }
    return byId;
  }

  /** Server wins: the local copy and its staged changes give way to `server`, or go when the server has none. */
  async replaceWithServer(expenseId: string, server: MeetingExpense | undefined): Promise<void> {
    const current = await this.getDoc(expenseId);
    if (!server || isExpenseCancelled(server)) {
      if (current) await this.removeCached(expenseId);
    } else {
      await this.putDoc({ expense: { ...server, notes: current?.expense.notes ?? [] } });
    }
  }

  async refreshNotes(expenseIds: string[]): Promise<void> {
    if (!expenseIds.length) return;
    const notes = await this.fetchNotes(expenseIds);
    for (const id of expenseIds) {
      const doc = await this.getDoc(id);
      if (doc) await this.putDoc({ ...doc, expense: { ...doc.expense, notes: notes.get(id) ?? [] } });
    }
  }

  async removeLocal(expenseId: string): Promise<void> {
    await this.removeCached(expenseId);
  }

  async fetchForSync(meetingWindow: MeetingSyncWindow, modifiedSinceIso: string | null): Promise<MeetingExpense[]> {
    const raws: any[] = await this.dynamics.executeFetchQuery<any>(
      MEETING_EXPENSE_ENTITY_SET,
      meetingExpenseSyncFetchXml(meetingWindow, modifiedSinceIso),
    );
    return this.withSyncedNotes((raws ?? []).map((raw) => meetingExpenseFromRaw(raw, meetingKey(raw[`_${MEETING_EXPENSE_FIELDS.REGARDING}_value`]))));
  }

  async fetchForSyncByIds(expenseIds: string[]): Promise<MeetingExpense[]> {
    return this.withSyncedNotes([...(await this.fetchServerExpenses(expenseIds)).values()]);
  }

  async fetchLiveIdsForSync(meetingWindow: MeetingSyncWindow): Promise<Set<string>> {
    const raws: unknown = await this.dynamics.executeFetchQuery<any>(MEETING_EXPENSE_ENTITY_SET, meetingExpenseSyncIdsFetchXml(meetingWindow));
    // An empty set would prune every cached expense, so a malformed response must fail the pull instead.
    if (!Array.isArray(raws)) throw new Error('Expense id sync returned no rows');
    return new Set(raws.map((raw) => raw[MEETING_EXPENSE_FIELDS.ID]));
  }

  async applySynced(cached: MeetingExpenseDoc[], expenses: MeetingExpense[], liveIds: Set<string>): Promise<void> {
    const { put, remove } = mergeSyncedExpenses(cached, expenses, liveIds);
    for (const doc of put) await this.cacheExpense(doc.expense);
    for (const id of remove) {
      if (!(await this.getDoc(id))?.pending) await this.removeCached(id);
    }
  }

  private async withSyncedNotes(expenses: MeetingExpense[]): Promise<MeetingExpense[]> {
    const liveIds = expenses.filter((expense) => !isExpenseCancelled(expense)).map((expense) => expense.id);
    const notes = await this.fetchNotes(liveIds).catch((err) => {
      console.error('MeetingExpenseDataService.fetchForSync notes failed', err);
      return new Map<string, IONote[]>();
    });
    return expenses.map((expense) => ({ ...expense, notes: notes.get(expense.id) ?? [] }));
  }

  /** The client id doubles as the annotation id, so a resend after a lost response can be recognised. */
  async createNote(expenseId: string, note: StagedSdrNote): Promise<string> {
    const body = {
      ...this.noteBody(note, false),
      ...(note.clientId ? { annotationid: note.clientId } : {}),
      [`${MEETING_EXPENSE_NAV.NOTE_PARENT}@odata.bind`]: `/${MEETING_EXPENSE_ENTITY_SET}(${expenseId})`,
    };
    const raw: any = await withTimeout(
      this.dynamics.create(body, ANNOTATIONS_ENTITY_SET, undefined, ['annotationid']),
      WRITE_TIMEOUT_MS,
      'Expense note create',
    );
    if (!raw?.annotationid) throw new Error('Expense note create returned no annotationid');
    return raw.annotationid;
  }

  async cacheNoteChange(expenseId: string, change: OnlineNoteChange): Promise<void> {
    const doc = await this.getDoc(expenseId);
    if (doc && !doc.pending) await this.putDoc(applyOnlineNoteChange(doc, change));
  }

  async fetchNoteIds(expenseId: string): Promise<Set<string>> {
    const notes = (await this.fetchNotes([expenseId])).get(expenseId) ?? [];
    return new Set(notes.map((note) => note.noteId.toLowerCase()));
  }

  async updateNote(annotationId: string, note: StagedSdrNote, clearAttachment: boolean): Promise<void> {
    await withTimeout(
      this.dynamics.update(annotationId, ANNOTATIONS_ENTITY_SET, this.noteBody(note, clearAttachment)),
      WRITE_TIMEOUT_MS,
      'Expense note update',
    );
  }

  async removeNote(annotationId: string): Promise<void> {
    await withTimeout(this.dynamics.delete(annotationId, ANNOTATIONS_ENTITY_SET), WRITE_TIMEOUT_MS, 'Expense note delete');
  }

  private recordKey(expenseId: string): string {
    return `${DB_KEY_PREFIXES.MEETING_EXPENSE_RECORD}${expenseId}`;
  }

  private async removeCached(expenseId: string): Promise<void> {
    await this.disk.remove(this.recordKey(expenseId));
    await this.trackPending(expenseId, false);
  }

  private async trackPending(expenseId: string, isPending: boolean): Promise<void> {
    for (const changes of this.recountChanges) changes.set(expenseId, isPending);
    if (!this.pendingIds) return this.recountPending();
    if (isPending) this.pendingIds.add(expenseId);
    else this.pendingIds.delete(expenseId);
    this.publishPendingCount();
  }

  private publishPendingCount(): void {
    this.disk.setOfflineDataCount(OFFLINE_DATA_COUNT_ENTITY_NAME.MEETING_EXPENSE, this.pendingIds?.size ?? 0);
  }

  // `keepNotes`: the form's copy omits notes still in flight, so the notes cached by their own writes win.
  private async cacheExpense(expense: MeetingExpense, keepNotes = false): Promise<void> {
    // A server copy must never overwrite changes the device has not uploaded yet.
    await this.disk.updateOrInsert(this.recordKey(expense.id), (existing) => {
      if (existing?.pending) return existing;
      const notes = keepNotes && existing?.expense ? existing.expense.notes : expense.notes;
      return { expense: toStoredExpense({ ...expense, notes }) };
    });
  }

  async allDocs(): Promise<MeetingExpenseDoc[]> {
    const docs = (await this.disk.batchFetch(DB_ALLDOCS_QUERY_OPTIONS.GET_ALL_MEETING_EXPENSE_RECORDS, true)) as MeetingExpenseDoc[];
    return (docs ?? []).filter((doc) => !!doc?.expense);
  }

  private async getDocsForMeeting(meetingId: string): Promise<MeetingExpenseDoc[]> {
    const target = meetingKey(meetingId);
    return (await this.allDocs()).filter((doc) =>
      meetingKey(this.resolveMeeting(doc.expense.meetingId) ?? doc.expense.meetingId) === target);
  }

  private async mutate(
    expenseId: string,
    change: (current: MeetingExpenseDoc | null) => MeetingExpenseDoc | null,
  ): Promise<MeetingExpenseDoc | null> {
    const current = await this.getDoc(expenseId);
    const next = change(current);
    if (next) await this.putDoc(next);
    else if (current) await this.removeCached(expenseId);
    return next;
  }

  private requireDoc(doc: MeetingExpenseDoc | null): MeetingExpenseDoc {
    if (!doc) throw new Error('Expense is not cached on this device');
    return doc;
  }

  private async fetchOptions(entitySet: string, fetchXml: string, idAttribute: string): Promise<ExpenseOption[]> {
    const response = await this.dynamics.executeFetchXml<any>(entitySet, fetchXml);
    return (response?.value ?? []).map((row) => ({ id: row[idAttribute], name: row.indskr_name ?? '' }));
  }

  private async fetchRaws(fetchXml: string): Promise<any[]> {
    const response = await this.dynamics.executeFetchXml<any>(MEETING_EXPENSE_ENTITY_SET, fetchXml);
    return response?.value ?? [];
  }

  private async fetchNotes(expenseIds: string[]): Promise<Map<string, IONote[]>> {
    const byExpense = new Map<string, IONote[]>();
    for (const chunk of chunkIds(expenseIds)) {
      const fetchXml = MEETING_EXPENSE_FETCH_XML.notesByObjectIds.replace('{idValues}', () => toFetchInValues(chunk));
      const response = await this.dynamics.executeFetchXml<any>(ANNOTATIONS_ENTITY_SET, fetchXml);
      for (const raw of response?.value ?? []) {
        const expenseId = raw['_objectid_value'];
        const note = new IONote({ ...raw, objectid: expenseId, ownerid: raw['_ownerid_value'], ownerName: raw[`_ownerid_value${FORMATTED}`] ?? '' } as any);
        byExpense.set(expenseId, [...(byExpense.get(expenseId) ?? []), note]);
      }
    }
    return byExpense;
  }

  private noteBody(note: StagedSdrNote, clearAttachment: boolean): Record<string, any> {
    const body: Record<string, any> = { notetext: note.noteText ?? '' };
    if (note.attachment) {
      body.documentbody = note.attachment.documentBody;
      body.filename = note.attachment.fileName;
      body.mimetype = note.attachment.mimeType;
      body.filesize = note.attachment.fileSize;
      body.isdocument = true;
    } else if (clearAttachment) {
      body.documentbody = null;
      body.filename = null;
      body.mimetype = null;
      body.isdocument = false;
    }
    return body;
  }
}
